import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuthentication, useFlightRooms } from '@connect-x-app/hybrid-state';
import { Channel } from '@connect-x-app/shared-domain-models';
import { checkIsUserScheduledForChannel } from '@connect-x-app/shared-utils';
import { Button, ConfirmationModal, SideModal, toastError } from '@connect-x-app/web-ui';

import { useOnlineCallback } from '../../hooks/useOnlineCallback';
import { UserInfoList } from './UserInfoList';

type Props = {
  channel: Channel;
  flightNumber: string;
  isVisible: boolean;
  onClose: () => void;
  roomId: string;
  showOfflineText?: boolean;
};

export const FlightRoomInfoSideModal = ({ channel, flightNumber, isVisible, onClose, roomId, showOfflineText }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthentication();
  const { leaveFlightRoom } = useFlightRooms();

  const isUserScheduledForChannel = user && checkIsUserScheduledForChannel(user.externalId, channel);

  const [isLeaveRoomModalOpen, setIsLeaveRoomModalOpen] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleLeaveFlightRoom = async () => {
    setIsLeaving(true);
    try {
      const result = await leaveFlightRoom(roomId);
      if (result?.ok) {
        navigate('/dashboard/flightrooms');
        onClose();
      } else {
        toastError(t('FLIGHTROOM.LEAVE.ERROR_ALERT.DESCRIPTION'));
      }
    } catch {
      toastError(t('FLIGHTROOM.LEAVE.ERROR_ALERT.DESCRIPTION'));
    } finally {
      setIsLeaving(false);
      setIsLeaveRoomModalOpen(false);
    }
  };

  const handleLeaveButtonClick = useOnlineCallback(
    () => setIsLeaveRoomModalOpen(prevState => !prevState),
    'ACTIONS.LEAVE_FLIGHTROOM',
  );

  return (
    <>
      <SideModal
        bottomButtons={
          !isUserScheduledForChannel ? (
            <Button disabled={isLeaving} isOutline onClick={handleLeaveButtonClick} theme="negative" type="button">
              {t('FLIGHTROOM.LEAVE.BUTTON')}
            </Button>
          ) : null
        }
        content={<UserInfoList channel={channel} showOfflineText={showOfflineText} />}
        header={{
          rightIcon: { iconName: 'closeCross', onClick: onClose },
          title: { text: t('FLIGHTROOM.DETAIL.TITLE') },
        }}
        isVisible={isVisible}
        onBackgroundClick={onClose}
      />
      {isLeaveRoomModalOpen ? (
        <ConfirmationModal
          cancelButtonTheme="dark"
          confirmButtonTheme="negative"
          confirmText={t('FLIGHTROOM.LEAVE.ALERT.SUBMIT')}
          content={t('FLIGHTROOM.LEAVE.ALERT.DESCRIPTION')}
          isLoading={isLeaving}
          onCancel={() => setIsLeaveRoomModalOpen(false)}
          onConfirm={handleLeaveFlightRoom}
          preventBackdropDismiss
          title={t('FLIGHTROOM.LEAVE.ALERT.TITLE', { name: flightNumber })}
        />
      ) : null}
    </>
  );
};
